import React, { useContext } from 'react';
import { Link } from 'react-router-dom';
import ConfigContext from '../../../contexts/ConfigContext';
import usePageContent, { CmsRichText } from '../../../hooks/usePageContent';
import CategoryPage from './CategoryPage';

function GyroCategory() {
  const { city_name, vehicle_type } = useContext(ConfigContext);
  const vehicleLowerCase = vehicle_type.toLowerCase();
  const { content: cms } = usePageContent('cuisine_gyro');

  const MainText = (
    <>
      <h1>{cms?.main?.heading || `Gyro food ${vehicleLowerCase}s in ${city_name}`}</h1>
      <CmsRichText
        html={cms?.main?.body}
        fallback={
          <p>Looking for a gyro, shawarma, falafel, or a Greek bowl? Browse { city_name } gyro and Mediterranean { vehicleLowerCase }s, check menus and schedules, or request catering for an event.</p>
        }
      />
    </>
  );

  const SecondaryText = (
    <>
      <h3 className='u-phosphate'>{cms?.secondary?.heading || 'What you’ll find'}</h3>
      <CmsRichText
        html={cms?.secondary?.body}
        fallback={
          <p>These { vehicleLowerCase }s cover Greek, Turkish, and Middle Eastern street food: gyro and shawarma wraps, falafel, rice bowls, hummus, and fries. Vegetarian and halal options are common, and a bowl is an easy gluten-free order. Check each menu for how dietary options are labeled.</p>
        }
      />

      { city_name === 'Seattle' && !cms &&
        <>
          <h3 className='u-phosphate u-mt4'>Seattle’s Best Gyro Food Trucks</h3>
          <p>Seattle is home to some truly exceptional Mediterranean food trucks, each offering their own spin on classic dishes.</p>
          <p><Link to='mr-gyros-food-truck'>Mr. Gyros Food Truck</Link> is a Seattle staple, serving up traditional Greek flavors with a focus on fresh ingredients and bold spices. Their gyros come packed with juicy lamb, beef, or chicken, and they offer vegetarian platters featuring hummus, falafel, and tabbouleh. This family-owned truck has been around since 2002, making it a go-to spot for Seattle locals looking for authentic Greek street food.</p>
          <p>For those who love a Greek twist with a bit of personality, <Link to='theo-s-gyros'>Theo’s Gyros</Link> is the place to be. This truck is known for its Greek-style gyros with a Seattle flair, featuring everything from roasted pork belly gyros to gyro fries loaded with feta cheese and tzatziki sauce. Their menu even includes hand-scooped falafel wraps and Greek bowls packed with basmati rice and seasonal veggies. With certified halal meats and fresh-made sauces, <Link to='theo-s-gyros'>Theo’s Gyros</Link> has quickly become a favorite for Mediterranean food lovers in Seattle.</p>
          <p>If you’re looking for Middle Eastern flavors with a focus on freshness, <Link to='falafel-salam'>Falafel Salam</Link> is a must-try. This truck is known for its scratch-made falafels, organic hummus, and shawarma plates. Their famous “Hesitator Plate” is perfect for indecisive foodies — it includes a mix of falafel, chicken shawarma, beef gyro, hummus, dolmas, pickled veggies, and fresh pita. Their menu is packed with vegan and gluten-free options, and their garlic fries topped with feta are a fan favorite.</p>
          <p>For those who love a fusion of Turkish and Mediterranean flavors, <Link to='hallava-falafel'>Hallava Falafel</Link> delivers. They specialize in homemade falafel sandwiches, slow-roasted lamb shawarma, and double-dropped fries served with hummus and tzatziki for dipping. Their pita pockets are baked fresh daily, ensuring that every bite is soft, warm, and delicious. If you’re in the mood for something lighter, their falafel salad is a great gluten-free option.</p>
        </>
      }

      <h3 className='u-phosphate u-mt4'>{cms?.events?.heading || 'Gyro catering for your event'}</h3>
      <CmsRichText
        html={cms?.events?.body}
        fallback={
          <p>Build-your-own wraps and bowls cover meat eaters, vegetarians, and gluten-free guests from one window. Tell us your date, location, and guest count. We’ll find { vehicleLowerCase }s that fit.</p>
        }
      />
    </>
  );

  return (
    <CategoryPage
      foodName='Gyros'
      foodCategory='mediterranean'
      seoTitle={cms?.meta?.title || `Gyro food ${vehicleLowerCase}s in ${city_name}`}
      seoDescription={cms?.meta?.description || `Find and book gyro food ${vehicleLowerCase}s in ${city_name}. Browse menus, compare quotes, and hire Mediterranean catering for your next event.`}
      seoCanonical='/gyro-food-trucks'
      MainText={MainText}
      SecondaryText={SecondaryText}
    />
  );
}

export default GyroCategory;
